import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { ButtonWithLoader } from "../../../general/ButtonWithLoader/ButtonWithLoader";
import { useKeyboardCommands } from "../../../../hooks/keyboard/useKeyboardCommands";
import { KeyboardReturnIcon } from "../../../../icons";
import { Shortcut } from "../../../commands/Shortcut";
import { Button } from "../../../general/Button/Button";
import {
    AddTransactionModalMutableConfig,
    AddTransactionsModalFormErrors,
    AddTransactionsModalFormikConfig,
} from "../AddTransactionsModal";
import styles from "./Table.module.scss";

interface Props {
    loading: boolean;
    addLines: () => void;
}

export const Footer: React.FC<Props> = ({ loading, addLines }) => {
    const formikContext = useFormikContext<AddTransactionsModalFormikConfig>();
    const { values, handleSubmit } = formikContext;
    const hideAfterSubmit = values.isHideAfterSubmit;
    const errors = formikContext.errors as AddTransactionsModalFormErrors;

    const onSubmit = useCallback(
        () => async () => {
            AddTransactionModalMutableConfig.validate = true;
            handleSubmit();
        },
        [handleSubmit],
    );

    useKeyboardCommands({
        commands: [
            {
                key: "Enter",
                requiresCtrlOrMeta: true,
                requiresShift: false,
                callback: () => {
                    onSubmit();
                },
            },
        ],
    });

    const shownError = useMemo(() => {
        if (errors.noTransactions) {
            return errors.noTransactions;
        }

        if (errors.financialAccountId) {
            return errors.financialAccountId;
        }

        if (errors.transactions && errors.transactions.length > 0) {
            const transactionErrorLines: string[] = [];

            (
                errors.transactions as Array<{
                    amount?: string;
                    date?: string;
                    category?: string;
                }>
            ).forEach((transaction) => {
                if (transaction.amount) {
                    transactionErrorLines.push(transaction.amount);
                }
                if (transaction.category) {
                    transactionErrorLines.push(transaction.category);
                }
                if (transaction.date) {
                    transactionErrorLines.push(transaction.date);
                }
            });

            if (transactionErrorLines.length > 1) {
                return "Please complete the highlighted fields";
            }

            if (transactionErrorLines.length === 1) {
                return transactionErrorLines[0];
            }
        }

        return undefined;
    }, [errors]);

    return (
        <div className={styles.modalFooter}>
            <div className={classNames(styles.footerButtons)}>
                <div>
                    <Button variant="secondary" onClick={addLines}>
                        Add lines
                    </Button>
                </div>

                <div className="d-flex align-items-center gap-buttons">
                    {shownError && (
                        <p className={classNames(styles.footerError)}>
                            {shownError}
                        </p>
                    )}
                    <ButtonWithLoader
                        loading={loading && hideAfterSubmit}
                        disabled={!!shownError}
                        variant="secondary"
                        onClick={onSubmit()}
                        type="submit"
                        tooltip={
                            <Shortcut
                                keys={["⌘", <KeyboardReturnIcon />]} // nosonar
                                hideText
                            />
                        }
                    >
                        Save
                    </ButtonWithLoader>
                </div>
            </div>
        </div>
    );
};
